import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import emailService from '../services/emailService';
import { fetchEmployees, type EmployeeListItem } from '../api/employees';

export default function SendEmail() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Données
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Formulaire
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const emps = await fetchEmployees();
      // Filtrer les employés avec email valide
      const filtered = emps.filter((e) => {
        const hasValidEmail = e.email && e.email.trim() !== '' && e.email !== 'NON RENSEIGNÉ';
        return hasValidEmail;
      });
      setEmployees(filtered);
    } catch (error) {
      console.error('Erreur chargement employés:', error);
      toast.error('Erreur lors du chargement des employés');
    } finally {
      setLoading(false);
    }
  };

  const toggleEmployee = (empId: string) => {
    if (selectedEmployees.includes(empId)) {
      setSelectedEmployees(selectedEmployees.filter(id => id !== empId));
    } else {
      setSelectedEmployees([...selectedEmployees, empId]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachmentFiles(Array.from(e.target.files));
    }
  };

  const removeFile = (index: number) => {
    setAttachmentFiles(attachmentFiles.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    // Validation
    if (selectedEmployees.length === 0) {
      toast.error('Veuillez sélectionner au moins un destinataire');
      return;
    }
    if (!subject.trim()) {
      toast.error('Veuillez saisir un objet');
      return;
    }
    if (!message.trim()) {
      toast.error('Veuillez saisir un message');
      return;
    }

    setSending(true);
    try {
      // Créer la campagne
      console.log('🚀 Création campagne avec:', {
        subject,
        message,
        employees: selectedEmployees,
        attachmentFiles: attachmentFiles.map(f => f.name)
      });

      const campaign = await emailService.create({
        subject,
        message,
        send_to_all: false,
        departments: [],
        employees: selectedEmployees,
        attachment_files: attachmentFiles,
      });

      console.log('✅ Campagne créée:', campaign);
      console.log('📧 ID de la campagne:', campaign.id);

      if (!campaign || !campaign.id) {
        throw new Error('La campagne n\'a pas été créée correctement');
      }

      // Envoyer immédiatement
      console.log('📤 Envoi de la campagne ID:', campaign.id);
      const result = await emailService.send(campaign.id);

      toast.success(`✓ Email envoyé à ${result.sent_count} destinataire(s)`);

      // Rediriger vers Fidex Email
      setTimeout(() => {
        navigate('/fidex-email');
      }, 1500);
    } catch (error: any) {
      console.error('Erreur envoi:', error);
      
      // Afficher un message d'erreur détaillé
      const errorData = error.response?.data;
      
      if (errorData?.error) {
        // Erreur structurée du backend
        let errorMessage = errorData.error;
        
        if (errorData.details) {
          errorMessage += `\n\n${errorData.details}`;
        }
        
        if (errorData.help) {
          errorMessage += `\n\n💡 ${errorData.help}`;
        }
        
        toast.error(errorMessage, { duration: 8000 });
      } else {
        // Erreur générique
        toast.error(error.response?.data?.error || error.message || 'Erreur lors de l\'envoi');
      }
    } finally {
      setSending(false);
    }
  };

  // Filtrage des employés
  const filteredEmployees = employees.filter((emp) =>
    emp.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (emp.email && emp.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-cyan-50 to-white p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link to="/" className="btn btn-ghost btn-sm hover:bg-yellow-100">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Retour
          </Link>
          <div className="flex items-center gap-3 bg-gradient-to-r from-yellow-400 to-cyan-400 text-black px-6 py-3 rounded-xl shadow-lg">
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <h1 className="text-2xl font-bold">Envoyer un Email</h1>
          </div>
        </div>

        <div className="card bg-white shadow-2xl border-t-4 border-yellow-400">
          <div className="card-body space-y-6">
            {/* 1. DESTINATAIRES */}
            <div>
              <label className="label">
                <span className="label-text font-semibold text-lg">Destinataires</span>
                <span className="label-text-alt">{selectedEmployees.length} sélectionné(s)</span>
              </label>

              {/* Recherche */}
              <input
                type="text"
                placeholder="Rechercher par nom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input input-bordered w-full mb-3"
              />

              {/* Liste des employés */}
              <div className="border-2 border-cyan-200 rounded-lg max-h-64 overflow-y-auto bg-white">
                {loading ? (
                  <div className="p-8 text-center">
                    <span className="loading loading-spinner text-cyan-500"></span>
                  </div>
                ) : filteredEmployees.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    {employees.length === 0
                      ? 'Aucun employé avec email trouvé'
                      : 'Aucun résultat'}
                  </div>
                ) : (
                  <table className="table table-sm">
                    <tbody>
                      {filteredEmployees.map((emp) => (
                        <tr
                          key={emp.id}
                          className={`cursor-pointer hover:bg-yellow-50 ${
                            selectedEmployees.includes(String(emp.id)) ? 'bg-cyan-50' : ''
                          }`}
                          onClick={() => toggleEmployee(String(emp.id))}
                        >
                          <td className="w-12">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-sm border-cyan-400 checked:border-cyan-500 [--chkbg:theme(colors.cyan.500)] [--chkfg:white]"
                              checked={selectedEmployees.includes(String(emp.id))}
                              onChange={() => toggleEmployee(String(emp.id))}
                            />
                          </td>
                          <td className="font-medium text-black">{emp.full_name}</td>
                          <td className="text-sm text-gray-600">{emp.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* 2. OBJET */}
            <div>
              <label className="label">
                <span className="label-text font-semibold text-lg">Objet</span>
              </label>
              <input
                type="text"
                placeholder="Objet de l'email"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="input input-bordered w-full"
              />
            </div>

            {/* 3. PIÈCES JOINTES */}
            <div>
              <label className="label">
                <span className="label-text font-semibold text-lg flex items-center gap-2 text-cyan-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  Pièces jointes
                </span>
              </label>

              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="file-input file-input-bordered border-cyan-300 w-full focus:border-cyan-500"
              />

              {attachmentFiles.length > 0 && (
                <div className="mt-2 space-y-1">
                  {attachmentFiles.map((file, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm bg-cyan-50 border border-cyan-200 p-2 rounded">
                      <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="flex-1 text-black">{file.name}</span>
                      <span className="text-xs text-gray-600">
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="btn btn-ghost btn-xs hover:bg-red-100 hover:text-red-600"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 4. MESSAGE */}
            <div>
              <label className="label">
                <span className="label-text font-semibold text-lg">Message</span>
              </label>
              <textarea
                placeholder="Votre message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="textarea textarea-bordered w-full h-48"
              />
            </div>

            {/* 5. BOUTON ENVOYER */}
            <div className="card-actions justify-end pt-4">
              <button
                onClick={handleSend}
                disabled={sending}
                className="btn btn-lg w-full sm:w-auto bg-gradient-to-r from-yellow-400 to-cyan-400 hover:from-yellow-500 hover:to-cyan-500 text-black font-bold border-none shadow-lg"
              >
                {sending ? (
                  <>
                    <span className="loading loading-spinner"></span>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    Envoyer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
